import * as React from 'react';
import { graphql } from 'gatsby';
import { GatsbySeo } from 'gatsby-plugin-next-seo';
import { useLocation } from '@reach/router';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';

import { Tile, Spinner, ProductFilters, Layout } from '../components';
import { CollectionHero } from '../components/collections';
import { useCollectionGraphQL, useThemeContext } from '../hooks';

export default function CollectionPageTemplate({
  data: { shopifyCollection },
}) {
  // set hero image
  const location = useLocation();
  const {
    heroImageLadies,
    heroImageMens,
    cutterAndBuckHeroImage,
    dailySportsHeroImage,
    eccoHeroImage,
    fit39HeroImage,
    gloveItHeroImage,
    jamieSadockHeroImageLadies,
    jamieSadockHeroImageMens,
    outerwearHeroImage,
    travisMathewHeroImage,
  } = useCollectionGraphQL();
  let heroImg;
  switch (shopifyCollection.title) {
    case 'Cutter & Buck Brand':
      heroImg = cutterAndBuckHeroImage;
      break;
    case 'Daily Sports':
      heroImg = dailySportsHeroImage;
      break;
    case 'Ecco Shoes Brand':
      heroImg = eccoHeroImage;
      break;
    case 'Fit39 Gloves Brand':
      heroImg = fit39HeroImage;
      break;
    case 'Glove It Brand':
      heroImg = gloveItHeroImage;
      break;
    case 'Jamie Sadock Brand':
      if (location.pathname.indexOf('ladies') === 1)
        heroImg = jamieSadockHeroImageLadies;
      else if (location.pathname.indexOf('mens') === 1)
        heroImg = jamieSadockHeroImageMens;
      break;
    case 'Outerwear':
      if (location.pathname.indexOf('ladies') === 1)
        heroImg = outerwearHeroImage;
      else if (location.pathname.indexOf('mens') === 1) heroImg = heroImageMens;
      break;
    case 'Travis Mathew':
      heroImg = travisMathewHeroImage;
      break;
    default:
      if (location.pathname.indexOf('ladies') === 1) heroImg = heroImageLadies;
      else if (location.pathname.indexOf('mens') === 1) heroImg = heroImageMens;
  }

  // Get theme out of context
  const { theme, setTheme } = useThemeContext();

  // Set theme
  React.useEffect(() => {
    if (location.pathname.indexOf('ladies') === 1) setTheme('ladies');
    if (location.pathname.indexOf('mens') === 1) setTheme('mens');
  }, [location.pathname, setTheme]);

  // Get all products from collection
  const { products } = shopifyCollection;

  // State for managing products
  const [prods, setProds] = React.useState([]);

  // State for managing products that match current theme
  const [themedProds, setThemedProds] = React.useState([]);

  //  State for managing size filter
  const [sizeFilter, setSizeFilter] = React.useState('All');

  // State for managing style filter
  const [styleFilter, setStyleFilter] = React.useState('All');

  // State for managing brand filter
  const [brandFilter, setBrandFilter] = React.useState('All');

  // Set items to show
  const INCREMENTOR = 8;
  const [itemsToShow, setItemsToShow] = React.useState(INCREMENTOR);

  // Update state whenever filter change
  React.useEffect(() => {
    // Start off with all products in the collection
    let tempProds = JSON.parse(JSON.stringify(products));

    // Then filter out products that do not match current theme
    if (theme === 'ladies') {
      tempProds = tempProds.filter(
        (prod) =>
          prod.tags.includes('Ladies') !== false ||
          prod.tags.includes('ladies') !== false
      );
    } else if (theme === 'mens') {
      tempProds = tempProds.filter(
        (prod) =>
          prod.tags.includes('Mens') !== false ||
          prod.tags.includes('mens') !== false
      );
    }
    setThemedProds(tempProds);

    // Then handle the size filter
    if (sizeFilter !== 'All') {
      tempProds = tempProds.filter(
        (prod) =>
          prod.options.find((option) => option.values.includes(sizeFilter)) !==
          undefined
      );
    }

    // Then handle the style filter
    if (styleFilter !== 'All') {
      tempProds = tempProds.filter(
        (prod) => prod.productType.includes(styleFilter) !== false
      );
    }

    // Then handle the brand filter
    if (brandFilter !== 'All') {
      tempProds = tempProds.filter(
        (prod) => prod.vendor.includes(brandFilter) !== false
      );
    }

    // Then set the prods array to the filtered products up to the correct amount
    setProds(tempProds);
  }, [products, itemsToShow, sizeFilter, styleFilter, brandFilter, theme]);

  // Set up infinite scroll
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  // If Spinner is visible, load more products
  React.useEffect(() => {
    if (inView)
      setItemsToShow((prev) =>
        prev + INCREMENTOR < prods.length ? prev + INCREMENTOR : prods.length
      );
  }, [inView, prods.length]);

  return (
    <Layout theme={theme}>
      <GatsbySeo
        title={`${shopifyCollection.title} Golf On Sale | Clothing | GLF Online | Womens and Mens`}
        description={shopifyCollection.description}
      />
      <div className="mt-9">
        <CollectionHero
          image={heroImg.childImageSharp.fluid}
          title={shopifyCollection.title}
        />
        <ProductFilters
          products={themedProds}
          setSizeFilter={setSizeFilter}
          setItemsToShow={setItemsToShow}
          setStyleFilter={setStyleFilter}
          setBrandFilter={setBrandFilter}
        />
        <div className="px-4 bg-white sm:px-6 lg:px-8">
          <div className="grid w-full max-w-6xl row-gap-6 col-gap-12 py-12 mx-auto md:grid-cols-2 lg:grid-cols-4 sm:py-16">
            {prods
              ? prods
                  .slice(0, itemsToShow)
                  .map((product) => (
                    <Tile
                      key={product.handle}
                      slug={`/${theme}/products/${product.handle}`}
                      title={product.title}
                      price={Number(product.priceRange.minVariantPrice.amount)}
                      image={product.images[0]}
                      constantPrice={
                        product.priceRange.minVariantPrice.amount ===
                        product.priceRange.maxVariantPrice.amount
                      }
                      available={product.availableForSale}
                      onSale={
                        shopifyCollection.title === 'Clearance Items' ||
                        parseFloat(product.variants[0].priceV2.amount) <
                          parseFloat(
                            product.variants[0].compareAtPriceV2?.amount
                          )
                      }
                    />
                  ))
              : Array(itemsToShow)
                  .fill('')
                  .map((_, i) => <div key={i}>Loading...</div>)}
            {prods && prods.length === 0 && (
              <p className="text-center md:col-span-2 lg:col-span-4">
                Sorry, no products found matching your search.
              </p>
            )}
          </div>
          {itemsToShow < prods.length && (
            <div
              ref={ref}
              className="relative flex items-center justify-center h-24 px-4 sm:px-6 lg:px-8"
            >
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

CollectionPageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
};

export const query = graphql`
  query CollectionPageTemplateQuery($handle: String!) {
    shopifyCollection(handle: { eq: $handle }) {
      id
      title
      handle
      description
      shopifyId
      products {
        tags
        title
        handle
        productType
        vendor
        availableForSale
        options {
          values
          name
        }
        priceRange {
          minVariantPrice {
            amount
          }
          maxVariantPrice {
            amount
          }
        }
        images {
          originalSrc
          id
        }
        variants {
          compareAtPriceV2 {
            amount
            currencyCode
          }
          priceV2 {
            amount
            currencyCode
          }
        }
      }
    }
  }
`;
